import React, { useState, useEffect } from 'react';
import api from '../api/api';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  GridLegacy as Grid,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Science as TestIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
} from '@mui/icons-material';

interface MpesaConfig {
  consumer_key: string;
  consumer_secret: string;
  passkey: string;
  business_shortcode: string;
  environment: string;
  callback_url: string;
  enabled: boolean;
}

interface MpesaStatus {
  enabled: boolean;
  configured: boolean;
  environment: string;
  business_shortcode: string;
  ready: boolean;
  error?: string;
}

export const MpesaSettingsPanel: React.FC = () => {
  const [config, setConfig] = useState<MpesaConfig>({
    consumer_key: '',
    consumer_secret: '',
    passkey: '',
    business_shortcode: '174379',
    environment: 'sandbox',
    callback_url: '',
    enabled: false,
  });
  
  const [status, setStatus] = useState<MpesaStatus>({
    enabled: false,
    configured: false,
    environment: 'unknown',
    business_shortcode: '',
    ready: false,
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testAmount, setTestAmount] = useState('100');

  useEffect(() => {
    loadMpesaConfig();
    loadMpesaStatus();
  }, []);

  const loadMpesaConfig = async () => {
    try {
      const response = await api.get('/settings/mpesa/config');
      const data = response.data;
      setConfig({
        consumer_key: data.consumer_key || '',
        consumer_secret: data.consumer_secret || '',
        passkey: data.passkey || '',
        business_shortcode: data.business_shortcode || '174379',
        environment: data.environment || 'sandbox',
        callback_url: data.callback_url || '',
        enabled: data.enabled || false,
      });
    } catch (err) {
      console.error('Failed to load M-Pesa config:', err);
    }
  };

  const loadMpesaStatus = async () => {
    try {
      const response = await api.get('/settings/mpesa/status');
      setStatus(response.data);
    } catch (err) {
      console.error('Failed to load M-Pesa status:', err);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/settings/mpesa/config', config);
      setSuccess('M-Pesa configuration saved successfully!');
      loadMpesaStatus();
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/settings/mpesa/test');
      const data = response.data;
      
      if (data.mpesa_test) {
        setSuccess('M-Pesa connection test successful!');
      } else {
        setError(data.error || 'Connection test failed');
      }
    } catch (err) {
      setError('Network error during connection test');
    } finally {
      setTesting(false);
    }
  };

  const handleTestStkPush = async () => {
    if (!testPhone || !testAmount) {
      setError('Please enter both phone number and amount');
      return;
    }

    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await api.post('/settings/mpesa/test-stk-push', {
        phone_number: testPhone,
        amount: parseFloat(testAmount),
      });

      const data = response.data;
      
      if (data.test_stk_push) {
        setSuccess('STK Push test initiated! Check the phone for M-Pesa prompt.');
        setTestDialogOpen(false);
      } else {
        setError(data.error || 'STK Push test failed');
      }
    } catch (err) {
      setError('Network error during STK Push test');
    } finally {
      setTesting(false);
    }
  };

  const getStatusColor = () => {
    if (status.ready) return 'success';
    if (status.configured) return 'warning';
    return 'error';
  };

  const getStatusText = () => {
    if (status.ready) return 'Ready';
    if (status.configured) return 'Configured (Disabled)';
    return 'Not Configured';
  };

  return (
    <Box>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h5" component="h2">
              M-Pesa Payment Settings
            </Typography>
          </Box>

          {/* Status Overview */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <Chip
                  icon={status.ready ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={getStatusText()}
                  color={getStatusColor() as any}
                  variant="outlined"
                />
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Environment: {status.environment}
                </Typography>
              </Grid>
              {status.business_shortcode && (
                <Grid item>
                  <Typography variant="body2" color="text.secondary">
                    Shortcode: {status.business_shortcode}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Configuration Form */}
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Consumer Key"
                value={config.consumer_key}
                onChange={(e) => setConfig({ ...config, consumer_key: e.target.value })}
                type={showSecrets ? 'text' : 'password'}
                helperText="M-Pesa API Consumer Key"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowSecrets(!showSecrets)}
                      edge="end"
                    >
                      {showSecrets ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Consumer Secret"
                value={config.consumer_secret}
                onChange={(e) => setConfig({ ...config, consumer_secret: e.target.value })}
                type={showSecrets ? 'text' : 'password'}
                helperText="M-Pesa API Consumer Secret"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowSecrets(!showSecrets)}
                      edge="end"
                    >
                      {showSecrets ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Passkey"
                value={config.passkey}
                onChange={(e) => setConfig({ ...config, passkey: e.target.value })}
                type={showSecrets ? 'text' : 'password'}
                helperText="M-Pesa API Passkey"
                InputProps={{
                  endAdornment: (
                    <IconButton
                      onClick={() => setShowSecrets(!showSecrets)}
                      edge="end"
                    >
                      {showSecrets ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Business Shortcode"
                value={config.business_shortcode}
                onChange={(e) => setConfig({ ...config, business_shortcode: e.target.value })}
                helperText="Paybill or Till Number"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Environment"
                value={config.environment}
                onChange={(e) => setConfig({ ...config, environment: e.target.value })}
                helperText="Sandbox for testing, Production for live payments"
              >
                <option value="sandbox">Sandbox (Testing)</option>
                <option value="production">Production (Live)</option>
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Callback URL"
                value={config.callback_url}
                onChange={(e) => setConfig({ ...config, callback_url: e.target.value })}
                helperText="URL for M-Pesa payment confirmations"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enabled}
                    onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  />
                }
                label="Enable M-Pesa Payments"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving}
              startIcon={saving ? <CircularProgress size={20} /> : <SettingsIcon />}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>

            <Button
              variant="outlined"
              onClick={handleTestConnection}
              disabled={testing || !status.configured}
              startIcon={testing ? <CircularProgress size={20} /> : <TestIcon />}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </Button>

            <Button
              variant="outlined"
              onClick={() => setTestDialogOpen(true)}
              disabled={!status.ready}
              startIcon={<PaymentIcon />}
            >
              Test STK Push
            </Button>
          </Box>

          {/* Help Information */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Setup Instructions
            </Typography>
            <Typography variant="body2" paragraph>
              1. Get your M-Pesa API credentials from Safaricom Developer Portal
            </Typography>
            <Typography variant="body2" paragraph>
              2. Configure your business shortcode (Paybill or Till Number)
            </Typography>
            <Typography variant="body2" paragraph>
              3. Set the callback URL to: <code>https://your-domain.com/api/mpesa/callback</code>
            </Typography>
            <Typography variant="body2" paragraph>
              4. Test the connection before enabling payments
            </Typography>
            <Typography variant="body2" color="warning.main">
              ⚠️ Use Sandbox environment for testing. Switch to Production only when ready for live payments.
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Test STK Push Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Test M-Pesa STK Push</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Send a test payment prompt to verify M-Pesa integration.
          </Typography>
          
          <TextField
            fullWidth
            label="Phone Number"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="0712345678"
            helperText="Enter phone number in format: 07XXXXXXXX"
            sx={{ mb: 2 }}
          />
          
          <TextField
            fullWidth
            label="Amount (KES)"
            value={testAmount}
            onChange={(e) => setTestAmount(e.target.value)}
            type="number"
            helperText="Test amount in Kenyan Shillings"
            sx={{ mb: 2 }}
          />
          
          <Alert severity="info">
            This will send a real M-Pesa prompt to the phone number. 
            Use a test phone number and small amount for testing.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTestStkPush}
            variant="contained"
            disabled={testing || !testPhone || !testAmount}
            startIcon={testing ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {testing ? 'Sending...' : 'Send Test Prompt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 